import type { BenefitDetailDto, BenefitWorkflowResponse } from "./benefit-types"
import { getBenefitProgramLabel, grantStatusLabels, proofingStatusLabels } from "./benefit-enums"

export interface BenefitActivity {
  key: string
  scope: "application" | "grants"
  scopeLabel: string
  source: string
  occurredAt: string
  event: string
  actorId: string | null
  actor: string
  explanation: string | null
  reason: string | null
  reasonGroup: string | null
  reasonCode: number | null
  statusCode?: number
  eventId?: string
  snapshotId?: string | null
}

/** A read-only projection. Do not infer current status, deduplicate records or synthesize events. */
export function benefitActivity(
  benefit: Pick<BenefitDetailDto, "statusHistory" | "benefitGrants">,
  workflow: BenefitWorkflowResponse | null,
): BenefitActivity[] {
  const records: BenefitActivity[] = (benefit.statusHistory ?? []).map((item, index) => ({
    key: `application-status-${index}`, scope: "application", scopeLabel: "Application", source: "Application status record",
    occurredAt: item.date, event: `Status recorded: ${proofingStatusLabels[item.status] ?? `Unknown status (${item.status})`}`,
    statusCode: item.status, actorId: item.updatedBy || null, actor: item.updatedByDisplayName || item.updatedBy || "Not recorded",
    explanation: null, reason: null, reasonGroup: null, reasonCode: null,
  }))
  for (const [grantIndex, grant] of benefit.benefitGrants.entries()) {
    const scopeLabel = `${getBenefitProgramLabel(grant.benefitProgramInstance.program)} · PY ${grant.benefitProgramInstance.year}`
    for (const [index, item] of (grant.statusHistory ?? []).entries()) records.push({
      key: `grant-status-${grantIndex}-${index}`, scope: "grants", scopeLabel, source: "Grant status record",
      occurredAt: item.date, event: `Status recorded: ${grantStatusLabels[item.grantStatus] ?? `Unknown status (${item.grantStatus})`}`,
      statusCode: item.grantStatus, actorId: item.updatedBy || null, actor: item.updatedByDisplayName || item.updatedBy || "Not recorded",
      explanation: null, reason: null, reasonGroup: null, reasonCode: null,
    })
  }
  // Missing/unknown authority must not make a workflow snapshot look like verified activity.
  if (workflow?.authority === "V2" && workflow.display?.readUnavailable !== true) {
    for (const [index, item] of (workflow.history ?? []).entries()) records.push({
      ...item, key: `workflow-${index}`, scope: "application", scopeLabel: "Application", source: "Application workflow",
      event: `${item.from} → ${item.to}`, actor: item.actorId || "Not recorded",
      reason: item.reasonCode == null ? null : workflow.reasons.find(reason => reason.group === item.reasonGroup && reason.code === item.reasonCode)?.meaning ?? `Reason ${item.reasonGroup ?? "unspecified"}/${item.reasonCode}`,
    })
    for (const [reviewIndex, review] of (workflow.grantReviews ?? []).entries()) {
      for (const [index, item] of (review.history ?? []).entries()) records.push({
        key: `review-${reviewIndex}-${item.eventId}-${index}`, scope: "grants",
        scopeLabel: `${getBenefitProgramLabel(review.programId)} · PY ${review.programYear}`, source: "Grant review",
        occurredAt: item.occurredAt, event: `${item.from} → ${item.to}`, actorId: item.actorId, actor: item.actorId || "Not recorded",
        explanation: item.explanation, reasonGroup: item.reasonGroup, reasonCode: item.reasonCode,
        // Grant-review codes are not the application-workflow reason catalogue.
        reason: item.reasonCode == null ? null : `Reason ${item.reasonGroup ?? "unspecified"}/${item.reasonCode}`,
        eventId: item.eventId, snapshotId: item.snapshotId,
      })
    }
  }
  return records.sort((a, b) => {
    const first = activityTimestamp(a.occurredAt), second = activityTimestamp(b.occurredAt)
    return first === second ? 0 : second - first
  })
}

function activityTimestamp(value: string): number {
  const timestamp = Date.parse(value)
  return Number.isFinite(timestamp) ? timestamp : -Infinity
}

export function activityDate(value: string): string {
  return Number.isFinite(Date.parse(value)) ? new Date(value).toLocaleString() : "Date unavailable"
}
